(ns minorodata.components.sql-console-modal.core
  (:require
   ["allotment" :refer [Allotment]]
   ["date-fns" :refer [differenceInSeconds]]
   ["humanize-duration" :as humanizeDuration]
   ["lucide-react" :refer [Check ChevronRight Code FilePlus2 LayoutList
                           Loader2 Menu Play Plus Save X]]
   ["smooth-scroll-into-view-if-needed" :as scroll-into-view]
   ["use-elapsed-time" :refer [useElapsedTime]]

   ; ["@assistant-ui/react" :refer [ThreadPrimitive AssistantIf  EditComposer UserMessage AssistantMessage]]

   [applied-science.js-interop :as jsi]
   [bleene.dag.context-menu :refer [launch-transform]]
   [clojure.string :as clojure.string]
   [goog.functions :refer [throttle]]
   [minorodata.app.config :refer [slow-query-duration-s]]
   [minorodata.app.handlers :refer [dispatch dispatch-sync]]
   [minorodata.app.posthog-events :as posthog-events]
   [minorodata.app.utils :as app.utils]
   [minorodata.components.dag.utils :refer [go-to-dag]]
   [minorodata.components.monaco.completions :as monaco.completions]
   [minorodata.components.monaco.diff-editor :as monaco.diff-editor]
   [minorodata.components.monaco.editor :as monaco.editor]
   [minorodata.components.spinner.core :as c.spinner]
   [minorodata.components.sql-console-modal.utils :refer [close-tab tab-id]]
   [minorodata.components.sql-console.core :as c.sql-console]
   [minorodata.components.sql-console.data-preview :as c.data-preview]
   [minorodata.components.sql-console.sidebar :as c.sql-console-sidebar]
   [minorodata.components.transformations.transforms :refer [revert-circular-dependency-modal
                                                             versions-list-modal]]
   [minorodata.components.transformations.utils :refer [go-to-logs
                                                        move-transform
                                                        move-transform-submenu
                                                        show-versions-history]]
   [minorodata.sql-console.model :refer [->sql-editor]]
   [minorodata.utils :as utils]
   [mold-forms.core :as form-component]
   [mold-forms.model :refer [->form-model]]
   [re-frame.core :as re-frame]
   [re-posh.core :as re-posh]
   [react :as react]
   [react-hotkeys-hook :refer [useHotkeys]]
   [reagent.core :as reagent]
   [reitit.frontend.easy :as rt.easy]
   [ui.button :refer [button]]
   [ui.chip]
   [ui.dialog :refer [dialog-content dialog-root dialog-title]]
   [ui.dropdown-menu :refer [dropdown-menu-content dropdown-menu-item
                             dropdown-menu-root dropdown-menu-separator
                             dropdown-menu-sub dropdown-menu-trigger]]
   [ui.fields :refer [input]]
   [ui.modal]
   [ui.shortcut]
   [ui.tabs]
   [ui.tooltip :refer [tooltip]]
   [ui.typography]
   [ui.utils :refer [clsx]]
   [oxbow.re-frame :as o]
   ; [oxbow.core :as o]
   ))


(declare scroll-tab-into-view)

(defn navigate-to-node [metadata]
  (go-to-dag {:transform/id        (:transform-id metadata)
              :transform/master-id (:master-id metadata)}))

(defn toggle-sidebar []
  (let [collapsed? @(re-posh/subscribe [:sql-editor/prop :sql-editor/sidebar-collapsed])]
    (dispatch [:sql-editor/update (->sql-editor {:sidebar-collapsed (not collapsed?)})])))


(defn create-new-tab []
  (let [tab-count @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-count])]
    (dispatch [:sql-console.events/create-new-tab])
    (dispatch [:sql-editor/update (->sql-editor {:current-tab (or tab-count 0)})])


    (scroll-tab-into-view tab-count)))

(defn update-selection' [editor e]
  (let [model     (jsi/call editor :getModel)
        selection (jsi/get e :selection)
        text      (jsi/call model :getValueInRange selection)]
    (dispatch [:sql-console.events/set-selection text])))

(def update-selection
  (throttle update-selection' 300))


(defn toggle-console-modal []
  (let [open? @(re-frame/subscribe [:sql-console-modal.subs/modal-open?])]
    (dispatch [:sql-console-modal.events/toggle-modal (not open?)])))

(defn sidebar [{:keys [width collapsed? toggle-sidebar]}]
  [:f> c.sql-console-sidebar/sidebar {:width          width
                                      :collapsed?     collapsed?
                                      :show-collapse-shortcut? true
                                      :toggle-sidebar toggle-sidebar}])


(defn save-tabs-to-localstorage []
  (dispatch [:sql-console.events/save-tabs-to-local-storage]))


(defn update-code-value' [value _evt]
  (let [tab-data    @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])
        current-tab (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/current-tab]) 0)]
    (dispatch [:sql-editor/update
               (->sql-editor {:tab-data-container (-> tab-data
                                                      (assoc-in [current-tab :code] value)
                                                      (update-in [current-tab :metadata] #(dissoc % :preview)))})])
    (save-tabs-to-localstorage)))

(def update-code-value
  (throttle update-code-value' 300))

(defn elapsed-time-ticker [params]
  (let [now   (js/Date.)
        since (js/Date. (jsi/get params :start))
        diff  (differenceInSeconds now since)
        elapsed-time (useElapsedTime #js {:isPlaying true :startAt diff})]
    (reagent.core/as-element
     (let [t (jsi/get elapsed-time :elapsedTime)
           show-slow-notice? (> t slow-query-duration-s)]
       [:<>
        (when show-slow-notice?
          [:div {:class ""}
           "This query is slow. We will notify you when it finishes."])
        [:div {:class [(when show-slow-notice? "ml-2")]}
         (if (> 0.5 t)
           "Starting..."
           (humanizeDuration (* 1000 t) #js {:round true}))]]))))


(def editor-ref c.sql-console/editor-ref)


(defn scroll-tab-into-view [tab-idx]
  ;; Giving it some time to finish rendering
  (js/setTimeout (fn []
                   (some-> (jsi/call js/document :getElementById (tab-id tab-idx))
                           (scroll-into-view #js {:behavior "smooth"})))
                 100))


(defn scroll-to-current-tab []
  (let [tab @(re-posh/subscribe [:sql-editor/prop :sql-editor/current-tab])]
    (when tab
      (scroll-tab-into-view tab))))


(defn transform-edited-warning [{:keys [metadata tab-data-container tab]}]
  (when (app.utils/date-before? (:opened-at metadata) (:updated-at metadata))
    (let [{:keys [transform-id]} metadata

          user    @(re-posh/subscribe [:user/pull])

          transform @(re-posh/subscribe [:transform/pull
                                         [:transform/id transform-id]
                                         '[* {:transform/updated-by [:searchable-user/username :searchable-user/id]}]])
          edited-yourself? (= (:user/uid user)
                              (get-in transform [:transform/updated-by :searchable-user/id]))]

      [:div {:class "absolute bottom-0 left-0 right-0 z-10 bg-kleene-warning-02 p-1 px-4 text-black/60 animate__animated animate__slideInUp animate__delay-1s"}
       (if edited-yourself?
         "You’ve edited this transform somewhere else, refresh to load the latest version."
         (str (or (get-in transform [:transform/updated-by :searchable-user/username])
                  "Someone")
              " has edited this transform, refresh to load the latest version."))

       [:span {:class "border border-kleene-warning-01 rounded-xs text-xs px-2 cursor-pointer ml-2"
               :on-click (fn []
                           (dispatch [:sql-console-modal.events/update-code-to-latest-version]))}
        "Replace"]])))


(defn transform-links [{:keys [metadata]}]
  (let [{:keys [group-id transform-id]} metadata
        group     @(re-posh/subscribe [:group/pull [:group/id group-id]])
        transform @(re-posh/subscribe [:transform/pull [:transform/id transform-id]])

        env-enabled? @(re-posh/subscribe [:environments.subs/enabled?])
        prod-env?    @(re-posh/subscribe [:environments.subs/production-active?])
        prod-env-id  app.utils/production-env-id

        env-id     (:environment-id metadata)
        env        (when env-id
                     @(re-posh/subscribe [:environments.subs/get-environment env-id]))]

    [:div

     (when env
       [:div {:class "absolute top-0 left-0 right-0 bottom-0 opacity-20 pointer-events-none z-1"
              :style {:background-color (:environment/colour env)}}])

     [:div {:class "flex flex-col"}
      [:a {:href  (rt.easy/href :transforms {:group-id group-id})
           :class "z-2 hover:opacity-60"
           :data-cy "sql-editor-group-link"
           :title "Go to group"
           :on-click (fn [e]
                       (.preventDefault e)

                       (when env-enabled?
                         (if env
                           (dispatch [:environments.events/select-env env-id {:source "sql-console"}])
                           (when-not prod-env?
                             (dispatch [:environments.events/select-env prod-env-id {:source "sql-console"}]))))

                       (dispatch [:sql-console-modal.events/toggle-modal false])

                       (js/setTimeout (fn []
                                        (dispatch [:app.events/navigate :transforms {:group-id group-id}]))

                                      ;; Need to give environment change some
                                      ;; time to resolve
                                      (if env-enabled?
                                        500
                                        0)))}

       (when env
         [:div {:class ""}
          [:span "Env: "]
          [ui.chip/chip (:environment/name env)]])

       [:div
        [:span {:class "font-semibold"} (:group/name group)]
        (str " / "
             (:transform/name transform))]]]]))


(defn save-or-create-transform
  "Tries to save transform if currently opened tab is a transform. Otherwise opens a Create transform modal to initiate transform creation workflow."
  ([] (save-or-create-transform {:ignore-parser false}))
  ([{:keys [ignore-parser]}]
   (let [tab-idx    (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/current-tab]) 0)
         tdc        @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])
         tab-data   (nth tdc tab-idx {})
         metadata   (:metadata tab-data)
         transform? (= "transform" (:type metadata))
         code       (:code tab-data)
         tab-id     (:tab-id metadata)]
     (when ignore-parser
       (dispatch-sync [:sql-console-modal.events/update-metadata-for-tab (:tab-id metadata) #(assoc % :ignore-parser ignore-parser)]))

     (if transform?
       (dispatch [:sql-console-modal.events/save-transform {:tab-data tab-data}])
       (do
         (dispatch
          [:transform.api/parse-sql-v2 {:sql          code
                                        :tab-id       tab-id
                                        :transform-id nil}])
         (c.sql-console/open-create-transform-drawer))))))


(defn custom-actions [{:keys [transform? monaco]}]
  (concat (c.sql-console/custom-actions {:monaco monaco})
          [{:id "save-as-transform"
            :label (if transform?
                     "Save"
                     "Save as Transform")
            :contextMenuOrder 0
            :contextMenuGroupId "2_execution"
            :keybindings [(bit-or (jsi/get-in monaco [:KeyMod :CtrlCmd])
                                  (jsi/get-in monaco [:KeyCode :KeyS]))]
            :run save-or-create-transform}

           {:id          "close-sql-console-modal"
            :label       "Close SQL Console"
            :contextMenuOrder 0
            :contextMenuGroupId "z_commands"
            :keybindings [(jsi/call-in monaco [:KeyMod :chord]
                                       (bit-or (jsi/get-in monaco [:KeyMod :CtrlCmd])
                                               (jsi/get-in monaco [:KeyCode :KeyK]))
                                       (bit-or (jsi/get-in monaco [:KeyMod :CtrlCmd])
                                               (jsi/get-in monaco [:KeyCode :KeyK])))]
            :run         (fn []
                           (toggle-console-modal))}]))


(defn custom-shortcuts [{:keys [monaco]}]
  [{:keybinding (bit-or (jsi/get-in monaco [:KeyMod :CtrlCmd])
                        (jsi/get-in monaco [:KeyCode :KeyI]))
    :command toggle-sidebar}])

(defn open-rename-query-dialog []
  (dispatch [:sql-editor/update {:sql-editor/rename-query-modal-open? true}]))

(defn rename-statement [tab-id name]
  (dispatch-sync [:sql-console-modal.events/update-metadata-for-tab
                  tab-id #(assoc % :name name)]))

(defn rename-query-modal-render []
  (let [open?     @(re-posh/subscribe [:sql-editor/prop :sql-editor/rename-query-modal-open?])
        tab-idx   (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/current-tab]) 0)
        tdc       @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])
        tab-data  (nth tdc tab-idx {})
        metadata  (:metadata tab-data)

        on-close  #(dispatch-sync [:sql-editor/update {:sql-editor/rename-query-modal-open? false}])
        on-submit (fn [fields]
                    (let [data (form-component/model->fields fields)]
                      (rename-statement (:tab-id metadata) (:name data))
                      (on-close)))]

    [dialog-root {:open open?
                  :on-open-change on-close}
     [dialog-content
      [dialog-title "Rename tab"]

      ^{:key (:tab-id metadata)}
      [form-component/form
       (->form-model
        {:id        :rename-query-form
         :fields    [{:name :name :value (or (:name metadata) "New statement")}]
         :on-submit on-submit})

       (fn [fields _ on-submit]
         [:div {:class " mt-2 flex flex-col gap-2"}
          (let [name-field (get fields :name)]
            [input {:field {:action (fn [evt]
                                      (let [new-name (jsi/get-in evt [:target :value])]
                                        (rename-statement (:tab-id metadata) new-name))
                                      ((:action name-field) evt))
                            :value (:value name-field)}
                    :autoFocus true
                    :label "Tab title"}])

          [:div {:class "flex justify-end"}
           [button {:on-click on-submit}
            "Save"]]])]]]))

(defn find-new-tab-index [arr final-x]
  (let [last-idx (dec (count arr))]
    (reduce
     (fn [_ {:keys [idx start-x end-x]}]
       (when-let [new-order (cond (and (= idx 0) (< final-x start-x))         0
                                  (and (= idx last-idx) (> final-x end-x))    last-idx
                                  (and (> final-x start-x) (< final-x end-x)) idx)]
         (reduced new-order)))
     nil
     arr)))

(defn get-all-tab-details [node]
  (->> (jsi/get-in node [:parentNode :childNodes])
       js->clj
       (map-indexed (fn [idx node]
                      (let [coords (.getBoundingClientRect node "x")]
                        {:idx     idx
                         :id      (.getAttribute node "data-id")
                         :start-x (jsi/get coords :left)
                         :end-x   (jsi/get coords :right)
                         :node    node})))))

(defn reorder-tabs-if-needed [{:keys [ev]}]
  (let [target      (jsi/get ev :target)
        final-x     (jsi/get ev :clientX)
        target-id   (.getAttribute target "data-id")
        tab-details (get-all-tab-details target)
        new-idx     (find-new-tab-index tab-details final-x)
        current-idx (:idx (utils/find-first #(= (:id %) target-id) tab-details))]
    (when new-idx
      (dispatch-sync [:sql-console-modal.events/reorder-tabs current-idx new-idx])
      (save-tabs-to-localstorage))))

(defn highlight-tab [ev]
  (let [target      (jsi/get ev :target)
        tab-details (get-all-tab-details target)
        final-x     (jsi/get ev :clientX)]
    (doall
     (for [{:keys [node start-x end-x]} tab-details]
       (if (and (>= final-x start-x)
                (<= final-x end-x))
         (jsi/call (jsi/get node :classList) :add "highlight")
         (jsi/call (jsi/get node :classList) :remove "highlight"))))))

(defn unhighlight-tabs [ev]
  (let [target      (jsi/get ev :target)
        tab-details (get-all-tab-details target)]
    (doall (for [{:keys [node]} tab-details]
             (jsi/call (jsi/get node :classList) :remove "highlight")))))

(defn set-tab [to-idx]
  (dispatch [:sql-editor/update (->sql-editor {:current-tab (or to-idx 0)})])

  (save-tabs-to-localstorage))

(defn tab-style [transform?]
  (if transform?
    {:icon  LayoutList
     :class "text-black/50 bg-[#D9C4FA]"}
    {:icon  Code
     :class "text-black/50 bg-[#56CFE1]"}))


;; Storing this in a temporary cahche location since we don't really need to
;; persist it in localstorage.
(defonce tab-scroll-position-cache (reagent/atom {}))
(defonce tab-cursor-position-cache (reagent/atom {}))

(defn save-tab-positions [tab-id cursor scroll]
  (swap! tab-cursor-position-cache assoc tab-id cursor)
  (swap! tab-scroll-position-cache assoc tab-id scroll))

(defn save-tab-editor-position [tab-id]
  (when @editor-ref
    (let [editor @editor-ref
          position (jsi/call editor :getPosition)
          first-line (-> (jsi/call editor :getVisibleRanges)
                         first
                         (jsi/get :startLineNumber))]

      (save-tab-positions tab-id position first-line))))


(defn restore-tab-editor-position [tab-id]
  (when @editor-ref
    (let [scroll-position (get @tab-scroll-position-cache tab-id 0)
          cursor-position (get @tab-cursor-position-cache tab-id)

          editor @editor-ref]

      (jsi/call editor :revealLine scroll-position)

      (when cursor-position
        (jsi/call editor :setPosition cursor-position)))))


(defn launch [metadata option-id]
  (let [id        (:transform-id metadata)
        group-id  (:group-id metadata)
        master-id (:master-id metadata)]
    (launch-transform id group-id option-id master-id)))

(defn get-latest-version [{:keys [metadata]}]
  (dispatch [:environments.api/get-latest-version-by-master-id-preview
             {:environment-id   (:environment-id metadata)
              :master-id        (:master-id metadata)
              :old-transform-id (:transform-id metadata)}]))

(defn code-editor []
  (let [loading? @(re-posh/subscribe [:mold-http/loading? :sql-console.api/post-query])

        save-loading? (or @(re-posh/subscribe [:mold-http/loading? :transform.api/parse-sql-v2])
                          @(re-posh/subscribe [:mold-http/loading? :transform.api/update-transform])
                          @(re-posh/subscribe [:mold-http/loading? :environments.api/update-environment-transform]))

        [show-diff set-show-diff] (react/useState false)
        [show-close-dialog set-show-close-dialog] (react/useState false)


        tab       (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/current-tab]) 0)
        tab-count @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-count])
        tdc       @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])

        tab-data  (nth tdc tab {})
        metadata  (:metadata tab-data)

        code      (:code tab-data)

        on-launch #(launch metadata %)

        selection           @(re-posh/subscribe [:sql-editor/prop :sql-editor/selection])
        validation-loading? @(re-posh/subscribe [:mold-http/loading? :sql-console.api/validate-query])
        selection-validity  @(re-posh/subscribe [:sql-editor/prop :sql-editor/selection-validity])

        transform? (= "transform" (:type metadata))

        output-tables-present? (seq (:output-tables metadata))
        should-display-tests?  (and transform?
                                    output-tables-present?
                                    (not (:environment-id metadata)))

        prod-transform      @(re-frame/subscribe [:transform.subs/get-preview-by-master-id (:master-id metadata)])
        blank-selection?    (clojure.string/blank? selection)

        env-enabled?       @(re-posh/subscribe [:environments.subs/enabled?])
        production-active? @(re-posh/subscribe [:environments.subs/production-active?])

        warehouse-type @(re-posh/subscribe [:warehouse-data/prop :warehouse-data/sink-type])
        snowflake?     (= warehouse-type "snowflake")

        selected-tab-id    (:tab-id metadata)

        scroll-position (when selected-tab-id (get @tab-scroll-position-cache selected-tab-id 0))

        missing-env? (and (= "transform" (:type metadata))
                          @(re-posh/subscribe [:environments.subs/invalid-env? (:environment-id metadata)]))]

    ; (useHotkeys "meta+s" create-new-tab)

    (react/useEffect
     (fn []
       (scroll-to-current-tab)

       (when (= "transform" (get-in tab-data [:metadata :type]))
         (get-latest-version tab-data))

       (fn []))
     #js [tab])

    (react/useEffect
     (fn []
       ;; (restore-tab-editor-position (:tab-id metadata))

       (fn []))
     #js [metadata])

    [:div {:class  "flex flex-col h-full w-full"}
     [:div {:class "flex justify-between items-center"
            :key   tab-count}

      [:div {:class "overflow-hidden"}
       [:div {:class "overflow-x-auto console-tabs"}
        [ui.tabs/tabs
         {:scrollable      true
          :value           tab
          :variant         :editor
          :size            :small
          :underlined      true
          :id              :modal-editor-tabs
          :activation-mode "manual"
          :on-value-change  (fn [new-idx]
                              (save-tab-editor-position (:tab-id metadata))
                              (set-tab new-idx))
          :tabs
          (mapv
           (fn [idx]
             (let [tab-data   (nth tdc idx {})
                   metadata   (:metadata tab-data)
                   env-id     (:environment-id metadata)
                   env        (when env-id
                                @(re-posh/subscribe [:environments.subs/get-environment env-id]))

                   transform? (= "transform" (:type metadata))
                   preview? (true? (:preview metadata))

                   missing-env? @(re-posh/subscribe [:environments.subs/invalid-env? env-id])
                   unsaved?     (when transform?
                                  (let [id             (get-in tab-data [:metadata :transform-id])
                                        transform-code @(re-frame/subscribe [:transform/prop [:transform/id id] :transform/command])]
                                    (and transform-code
                                         (not= transform-code (:code tab-data)))))

                   {:keys [icon class]} (tab-style transform?)]

               {:data-id     idx
                :value       idx
                :disabled    loading?
                :class       (clsx "[&.highlight]:bg-kleene-selected-background" (when missing-env? "opacity-50"))
                :draggable   true
                :on-click    (fn [e]
                               ;; Doubleclick
                               (when (= (jsi/get e :detail) 2)
                                 (dispatch [:sql-console-modal.events/update-metadata-for-tab (:tab-id metadata) #(dissoc % :preview)])))
                :on-drag     highlight-tab
                :on-drag-end (fn [ev]
                               (unhighlight-tabs ev)
                               (reorder-tabs-if-needed {:ev   ev
                                                        :tabs (range tab-count)}))
                :label       [:div {:id       (tab-id idx)
                                    :class    (clsx "flex gap-1 items-center leading-5 whitespace-nowrap text-xs"
                                                    {"italic" preview?})
                                    :on-double-click (fn []
                                                       (when-not transform?
                                                         (open-rename-query-dialog)))}

                              (let [icon-comp [:div {:class ["mr-1 rounded-xs w-5 h-5 z-10 inline-flex items-center justify-center"
                                                             (when-not transform?
                                                               "-my-1")
                                                             class]
                                                     :style {:background-color (when env (:environment/colour env))}}
                                               [:> icon {:size         16
                                                         :class        (clsx "leading-zero")}]]]

                                (if transform?
                                  [tooltip
                                   {:trigger-as-child true

                                    :trigger-props {:class "-my-1"}
                                    :content-props {:side "bottom"}

                                    :content [transform-links {:metadata metadata}]}
                                   icon-comp]

                                  icon-comp))

                              (if transform?
                                [:span {:title (str (:name metadata)
                                                    (if env
                                                      (str  " in " (:environment/name env) " environment")
                                                      ""))
                                        :class "truncate max-w-[150px]"}
                                 (or (:name metadata) "Transform")]
                                [:span (or (:name metadata) "New statement")])

                              [:div {:class "inline-flex relative group items-center justify-center w-5 h-5 -my-1 rounded-xs hover:bg-black/10 hover:text-kleene-error-01! dark:hover:text-kleene-error-01! text-kleene-dark-05 dark:text-white/80 transition-colors!"}
                               (when unsaved?
                                 [:div {:class "absolute rounded-full h-3 w-3 bg-kleene-text group-hover:hidden"}])
                               [:> X {:size     16
                                      :class    (clsx "leading-zero"
                                                      {"hidden group-hover:block" unsaved?})
                                      :on-click (fn [evt]
                                                  (if (and transform? unsaved?)
                                                    (set-show-close-dialog idx)
                                                    (close-tab idx))

                                                  (.stopPropagation evt))}]]

                              (when env
                                [:div {:class "absolute top-0 left-0 right-0 bottom-0 opacity-20 pointer-events-none z-2"
                                       :style {:background-color (:environment/colour env)}}])]}))
           (range tab-count))}]]]

      [:div {:class "flex gap-2 px-2"}
       [tooltip {:trigger-as-child false
                 :content [ui.shortcut/tooltip-content
                           {:content "New tab"}]}
        [button {:size       :icon
                 :variant    :empty
                 :data-cy    "sql-editor-new-tab-btn"
                 :on-click   create-new-tab
                 :title      "New tab"
                 :icon       Plus
                 :component  :div}]]

       [dropdown-menu-root
        [dropdown-menu-trigger {:as-child true
                                :disabled missing-env?}
         [:div
          [button {:size       :icon
                   :variant    :empty
                   :disabled   missing-env?
                   :data-cy    "sql-editor-more-btn"
                   :icon       Menu}]]]

        [dropdown-menu-content
         (when transform?
           [dropdown-menu-sub
            {:content [:<>
                       [dropdown-menu-item
                        {:on-click #(on-launch :single)}
                        "Only this one"]

                       [dropdown-menu-item
                        {:on-click #(on-launch :upstream)}
                        "Upstream"]

                       [dropdown-menu-item
                        {:on-click #(on-launch :downstream)}
                        "Downstream"]]}

            [:span {:class "flex items-center gap-2 justify-between w-full"}
             [:span "Run"]
             [:> ChevronRight {:size 16}]]])

         (when transform?
           (let [env-id     (:environment-id metadata)
                 env        (when env-id
                              @(re-posh/subscribe [:environments.subs/get-environment env-id]))


                 current-env-id @(re-posh/subscribe [:sql-console-modal.subs/current-environment-id])

                 disable-settings? (boolean (and env-enabled?
                                                 (or (and production-active? env)
                                                     (and (not production-active?)
                                                          (= env-id current-env-id)))))]

             [dropdown-menu-item {:data-cy  "sql-console-modal-transform-settings-btn"
                                  :disabled disable-settings?
                                  :on-click (fn []
                                              (dispatch [:app.events/navigate-query {:transform-settings true
                                                                                     :transform-id       (:transform-id metadata)
                                                                                     :group-id           (:group-id metadata)}]))}
              [:span {}
               "Settings..."
               (when disable-settings?
                 [:<>
                  [:br]
                  [:small "Wrong env is active"]])]]))

         (when transform?
           [move-transform-submenu {:title        "Move transform"
                                    :on-move      #(move-transform
                                                    (:transform-id metadata) %
                                                    (fn []
                                                      (get-latest-version tab-data)
                                                      (dispatch [:environments.api/fetch-groups])
                                                      (dispatch [:environments.api/fetch-transforms {:group-id %
                                                                                                     :replace? true}])
                                                      (dispatch [:environments.api/fetch-transforms {:group-id (get-in tab-data [:metadata :group-id])
                                                                                                     :replace? true}])
                                                      (dispatch [:sql-console.events/save-metadata-to-local-storage])))
                                    :on-new-group #(dispatch [:app.events/navigate-query
                                                              {:create-group   true
                                                               :move-transform (:transform-id metadata)}])}])

         (when transform?
           [dropdown-menu-item {:on-click (fn []
                                            (go-to-logs metadata)
                                            (toggle-console-modal))}
            "Show in Logs"])

         (when transform?
           [dropdown-menu-item {:on-click (fn []
                                            (navigate-to-node metadata)
                                            (toggle-console-modal))}
            "Show in Pipelines"])

         (when transform?
           (let [transform @(re-posh/subscribe [:transform/pull [:transform/id (:transform-id metadata)]])]
             [dropdown-menu-item {:on-click #(show-versions-history
                                              (app.utils/drop-ns-in-keys transform)
                                              [:sql-console-modal.events/update-code-to-latest-version])}
              "Show history"]))

         (when should-display-tests?
           [dropdown-menu-item {:data-cy    "sql-console-modal-add-unit-tests-btn"
                                :on-click   (fn []
                                              (dispatch [:sql-console-modal.events/toggle-unit-tests-modal true])
                                              (dispatch [:sql-console.modal.events/set-unit-tests-transform (:transform-id metadata)]))}
            "Add unit tests"])

         [dropdown-menu-item {:on-click c.sql-console/open-create-data-view-modal
                              :disabled (not @(re-posh/subscribe [:users.subs/has-permissions? :data-views/write]))
                              :data-cy  "sql-editor-create-data-view-btn"}
          "Create data view"]

         [dropdown-menu-item {:data-cy  "sql-console-modal-format-btn"
                              :on-click (fn []
                                          (dispatch [:sql-console.events/set-editor-content (app.utils/format-code code)]))}
          "Format"]

         (when transform?
           [dropdown-menu-item {:data-cy  "sql-console-modal-diff-btn"
                                :on-click (fn []
                                            (when (and (not show-diff) (:environment-id metadata))
                                              (dispatch [:posthog/capture {:event (:transform-environment/compared posthog-events/events)
                                                                           :props {:environment-id (:environment-id metadata)
                                                                                   :source         "sql-console"}}]))
                                            (set-show-diff (not show-diff)))}
            (if show-diff
              "Hide diff"
              "Show diff")])

         (when (:environment-id metadata)
           [dropdown-menu-item {:data-cy  "sql-console-modal-copy-db-btn"
                                :on-click (fn []
                                            (dispatch [:sql-console-modal.events/set-db-copy-env (:environment-id metadata)])
                                            (dispatch [:sql-console-modal.events/toggle-db-copy-modal true]))}

            "Copy data"])

         [dropdown-menu-item {:data-cy  "sql-console-modal-validate-btn"
                              :disabled  (or validation-loading? blank-selection?)
                              :on-click (fn []
                                          (if blank-selection?
                                            (let [text @(re-posh/subscribe [:sql-editor/prop :sql-editor/code])]
                                              (dispatch [:sql-console.api/validate-query text]))

                                            (let [model         (jsi/call @editor-ref :getModel)
                                                  selection     (jsi/call @editor-ref :getSelection)
                                                  selected-text (jsi/call model :getValueInRange selection)]
                                              (dispatch [:sql-console.api/validate-query selected-text]))))}

          [:div {:class "flex gap-2"}
           (when validation-loading?
             [c.spinner/inline-configurable {:size 14}])

           (cond
             (= :valid selection-validity)   [:> Check {:size 14}]
             (= :invalid selection-validity) [:> X {:size 14}]

             :else nil)

           "Validate selection"]]

         (when-not transform?
           [dropdown-menu-item {:on-click open-rename-query-dialog}
            "Rename tab"])

         (when snowflake?
           [:<>
            [dropdown-menu-separator]

            [dropdown-menu-item {:data-cy "slq-console-modal-open-json-flattening-btn"
                                 :on-click #(dispatch [:app.events/navigate-query {:json-flattening true}])}
             "Open SQL Generator"]])

         (when transform?
           [dropdown-menu-item {:data-cy  "sql-console-modal-delete-transform-btn"
                                :class    "text-kleene-error-01"
                                :on-click (fn []

                                            (dispatch [:app.events/navigate-query {:delete-transform true
                                                                                   :transform-id     (:transform-id metadata)
                                                                                   :group-id         (:group-id metadata)
                                                                                   :tab-id           (:tab-id metadata)}]))}
            "Delete transform"])]]

       (when transform?
         [tooltip {:trigger-as-child false
                   :content [ui.shortcut/tooltip-content
                             {:content "Save transform"
                              :keys ["cmd" "s"]}]}
          [button {:size       :icon
                   :variant    :empty
                   :data-cy    "sql-editor-save-transform-separate-btn"
                   :disabled   (or missing-env? save-loading?)
                   :on-click   (fn []
                                 (dispatch [:sql-console-modal.events/save-transform {:tab-data (nth tdc tab {})}]))
                   :title      "Save transform"
                   :icon       Save
                   :component  :div}]])

       (when-not transform?
         [tooltip {:trigger-as-child false
                   :content [ui.shortcut/tooltip-content
                             {:content "Save as transform"
                              :keys ["cmd" "s"]}]}
          [button {:size       :small
                   :variant    :empty
                   :class      "py-1"
                   :data-cy    "sql-editor-save-as-transform-separate-btn"
                   :disabled   (or missing-env? save-loading?)
                   :on-click   (fn []
                                 (dispatch
                                  [:transform.api/parse-sql-v2 {:sql          code
                                                                :tab-id       (:tab-id metadata)
                                                                :transform-id nil}])
                                 (c.sql-console/open-create-transform-drawer))
                   :title      "Save as transform"
                   :icon       FilePlus2
                   :component  :div}
           "Transform"]])

       [tooltip {:trigger-as-child false
                 :content [ui.shortcut/tooltip-content
                           {:content (if blank-selection?
                                       "Preview all"
                                       "Preview selection")
                            :keys ["cmd" "Enter"]}]}
        [button {:size    :icon
                 :icon (if loading? Loader2 Play)
                 :icon-props (when loading? {:class "animate-spin"})
                 :variant  :primary
                 :disabled (or loading? missing-env?)
                 :class    "pl-2"
                 :data-cy  "sql-editor-run-button"
                 :title    (if blank-selection?
                             "Preview all"
                             "Preview selection")
                 :on-click c.sql-console/execute-command
                 :component :div}]]]]

     [:div {:class "h-full w-full grow relative overflow-hidden"}

      (if (and show-diff transform?)
        [:> Allotment {:min-size 200}
         [:div {:class "h-full w-full"}
          ^{:key (:tab-id metadata)}
          [:f> monaco.editor/editor
           {:line                (or scroll-position 0)
            :data-cy             "sql-console-editor"
            :language            (monaco.editor/warehouse-language warehouse-type)
            :on-change           update-code-value
            :editor-will-mount   (fn [monaco]
                                   (monaco.completions/init-completions-provider monaco))
            :editor-did-mount    (fn [editor monaco]
                                   (reset! editor-ref editor)
                                   (jsi/call editor :focus)
                                   (jsi/call editor "onDidChangeCursorSelection" (partial update-selection editor))

                                   (doall
                                    (for [action (custom-actions {:transform? transform? :monaco monaco})]
                                      (jsi/call editor :addAction (clj->js action))))

                                   (mapv (fn  [{:keys [keybinding command]}]
                                           (jsi/call editor :addCommand keybinding command ""))
                                         (custom-shortcuts {:monaco monaco})))
            :editor-will-unmount save-tabs-to-localstorage
            :options             {:fontSize        12
                                  :automaticLayout true
                                  :contextmenu     true}
            :value               (or code "")}]]
         [:div {:class "h-full w-full"}
          [:div {:class "bg-kleene-warning-02 p-1 flex flex-row items-center"}
           [:span {:class "grow"} "Comparing with latest Production version"]
           [button {:icon     X
                    :on-click #(set-show-diff false)
                    :class    "bg-kleene-warning-02 hover:bg-kleene-warning-01/80 border border-kleene-warning-01"
                    :variant  :empty
                    :size     :icon}]]
          [:f> monaco.diff-editor/diff-editor
           {:original       (or (:command prod-transform) "")
            :modified       (or code "")
            :layout-timeout 100}]]]

        [:<>
         ^{:key (str (:tab-id metadata)
                     (:touched-at metadata))}
         [:f> monaco.editor/editor
          {:line                (or scroll-position 0)
           :language            (monaco.editor/warehouse-language warehouse-type)
           :theme               "vs-dark"
           :data-cy             "sql-console-editor"
           :on-change           update-code-value
           :editor-will-mount   (fn [monaco]
                                  (monaco.completions/init-completions-provider monaco))
           :editor-did-mount    (fn [editor monaco]
                                  (reset! editor-ref editor)
                                  (jsi/call editor :focus)
                                  (jsi/call editor "onDidChangeCursorSelection" (partial update-selection editor))

                                  (doall
                                   (for [action (custom-actions {:transform? transform? :monaco monaco})]
                                     (jsi/call editor :addAction (clj->js action))))

                                  (mapv (fn  [{:keys [keybinding command]}]
                                          (jsi/call editor :addCommand keybinding command ""))
                                        (custom-shortcuts {:monaco monaco})))

           :editor-will-unmount save-tabs-to-localstorage
           :options             {:fontSize        12
                                 :automaticLayout true
                                 :contextmenu     true}
           :value               (or code "")}]])]

     (cond
       missing-env?
       [:div {:class "bg-gray-300 p-1"}
        "Transform's environment has been removed. This tab is in read-only mode."]

       (= "transform" (:type metadata))
       ^{:key (str metadata)}
       [transform-edited-warning {:metadata metadata
                                  :tab-data-container tdc
                                  :tab tab}])

     [dialog-root {:open           (boolean show-close-dialog)
                   :on-open-change (fn [open?]
                                     (when-not open?
                                       (set-show-close-dialog false)))}
      [dialog-content
       (let [transform-name (get-in tdc [tab :metadata :name])
             long-title? (> (count transform-name) 55)]
         [dialog-title
          {:class "max-w-full min-w-0"}

          (if long-title?
            [:span {:class "flex flex-wrap items-baseline gap-x-1"}
             [:span "Save changes made to"]

             ;; NOTE: Using explicit WebKit clamp styles instead of Tailwind line-clamp
             ;; because it behaves more reliably inside flex-based dialog layouts
             ;; (especially with min-w-0 + Allotment split panes).
             [:span
              {:class "min-w-0 max-w-full break-all overflow-hidden uppercase"
               :style {:display "-webkit-box"
                       :-webkit-line-clamp 2
                       :-webkit-box-orient "vertical"
                       :white-space "normal"}}
              (str "'" transform-name)]

             [:span {:class "shrink-0 whitespace-nowrap"} "' transform?"]]
            [:span
             "Save changes made to '"
             [:span {:class "uppercase"} transform-name]
             "' transform?"])])
       [:div {:class "mt-2"}
        "Your changes will be lost if you don't save them."]
       [:div {:class "mt-4 flex justify-end gap-2"}

        [button {:variant  :secondary
                 :on-click (fn []
                             (set-show-close-dialog false))}
         "Cancel"]
        [button {:variant  :error
                 :on-click (fn []
                             (close-tab show-close-dialog)
                             (set-show-close-dialog false))}
         "Close without saving"]
        [button {:on-click (fn []
                             (dispatch [:sql-console-modal.events/save-transform-and-close {:idx      show-close-dialog
                                                                                            :tab-data (nth tdc show-close-dialog {})}])
                             (set-show-close-dialog false))}
         "Save and close"]]]]]))

(defn remove-ansi-escape-chars [s]
  (clojure.string/replace (str s) #"\u001b[^m]*?m" ""))


(defn transform-parsing-error []
  (let [tab-idx    (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/current-tab]) 0)
        tdc        @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])
        tab-data   (nth tdc tab-idx {})
        metadata   (:metadata tab-data)
        transform? (= "transform" (:type metadata))
        message    (:transform-parsing-error metadata)]
    [:div {:class "p-2 h-full flex flex-col"}

     [:div {:class "bg-kleene-error-05 text-kleene-error-01 p-2 rounded-md h-full overflow-y-auto"}

      [:div
       [ui.typography/h2 "Failed to parse the " (if transform? "transform" "SQL statement")]

       (into  [:pre]
              (if (string? message)
                [(remove-ansi-escape-chars message)]
                (map remove-ansi-escape-chars message)))

       (when transform?
         [:div {:class "mt-6"}
          [button {:on-click (fn []
                               (save-or-create-transform {:ignore-parser true}))
                   :variant :error
                   :class "text-white"}
           "Ignore errors and save"]])]]]))



(defn data-preview []
  (let [tab      (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/current-tab]) 0)
        tdc      @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])
        tab-data (nth tdc tab {})
        metadata (:metadata tab-data)
        failed?  (:failed? tab-data)]
    (cond
      (:transform-parsing-error metadata)
      [transform-parsing-error]

      failed?
      [c.sql-console/error-message-preview]

      :else
      [:f> c.data-preview/data-preview])))


(defn console []
  (let [outer-ref (react/useRef nil)

        tab        (or @(re-posh/subscribe [:sql-editor/prop :sql-editor/current-tab]) 0)
        tdc        @(re-posh/subscribe [:sql-editor/prop :sql-editor/tab-data-container])
        tab-data   (nth tdc tab {})
        metadata   (:metadata tab-data)
        collapsed? @(re-posh/subscribe [:sql-editor/prop :sql-editor/sidebar-collapsed])]

    (useHotkeys "mod+i" (fn [e]
                          (.preventDefault e)
                          (toggle-sidebar)))

    (useHotkeys "mod+s" (fn [e]
                          (.preventDefault e)
                          (save-or-create-transform)))


    (react/useEffect
     (fn []
       (.reset (.-current outer-ref))
       (fn []))
     #js [collapsed?])

    [:<>
     ^{:key :query-history-editor}
     [c.sql-console/drawers-render]
     ^{:key :create-data-view}
     [c.sql-console/create-data-view-modal-render]

     ^{:key (:tab-id metadata)}
     [rename-query-modal-render]

     [:div {:class "grow flex flex-row h-full"}
      [:> Allotment {:ref           outer-ref
                     :min-size      275}

       [:> Allotment.Pane {:min-size       (if collapsed? 41 300)
                           :max-size       (if collapsed? 41 js/Infinity)
                           :preferred-size (if collapsed? 41 300)}
        [:div {:class "h-full w-full"}
         [sidebar {:collapsed? collapsed?
                   :toggle-sidebar toggle-sidebar}]]]
       [:div {:class "h-full w-full flex flex-col"}
        [:> Allotment {:vertical      true
                       :min-size      200
                       :default-sizes [70 30]}
         [:div {:class "h-full w-full"}
          [:f> code-editor]]
         [:div {:class "h-full w-full overflow-hidden"}
          [data-preview]]]]]]]))


(defn footer []
  (let [max-rows-option     @(re-posh/subscribe [:sql-editor/prop :sql-editor/max-rows-count])

        loading?            @(re-posh/subscribe [:mold-http/loading? :sql-console.api/post-query])

        query-runtime       @(re-frame/subscribe [:sql-console.data/query-time])
        query-start-time    @(re-frame/subscribe [:sql-editor/prop :sql-editor/query-start-time])

        show-more-rows?     @(re-posh/subscribe [:unleash/is-enabled "fe.milion-rows"])]

    [:footer {:class "bg-kleene-secondary-02 text-white h-7 pl-2 pr-14 pt-1 rounded-b flex gap-2 justify-between"}

     [:div {:class "flex flex-col"}
      [:div {:class "flex items-center"}

       [:div {:class ["mr-2" "whitespace-nowrap"]
              :title (str "Results limited to first " (or max-rows-option c.sql-console/DEFAULT_MAX_ROWS) " rows")}
        "Limit to"]

       [ui.fields/select {:field       {:action  c.sql-console/update-max-rows-option
                                        :value   (str (or max-rows-option c.sql-console/DEFAULT_MAX_ROWS))}
                          :title       (str "Results limited to first " (or max-rows-option c.sql-console/DEFAULT_MAX_ROWS) " rows")
                          :field-props {:class "py-0 min-h-0"}
                          :native      false
                          :variant     :text
                          :options     (filter some? [{:value "200" :text "200 rows"}
                                                      {:value "2000" :text "2,000 rows"}
                                                      {:value "20000" :text "20,000 rows"}
                                                      (when show-more-rows? {:value "100000" :text "100,000 rows"})
                                                      (when show-more-rows? {:value "250000" :text "250,000 rows"})
                                                      (when show-more-rows? {:value "500000" :text "500,000 rows"})
                                                      (when show-more-rows? {:value "1000000" :text "1,000,000 rows"})
                                                      (when show-more-rows? {:value "10000000" :text "10,000,000 rows"})])}]]]


     [:div {:class "flex gap-2"}
      (when-not loading?
        [:div
         (if (some? query-runtime)
           (str "Query finished in " query-runtime)
           (str "Results limited to first " (or max-rows-option c.sql-console/DEFAULT_MAX_ROWS) " rows"))])

      (when loading?
        [:div {:class "flex"}
         [:> elapsed-time-ticker {:start query-start-time}]])]]))


(defn console-modal []
  (react/useEffect
   (fn []
     (c.sql-console/init-sql-editor-state)

     (let [handle-envs? @(re-posh/subscribe [:unleash/is-enabled "fe.environments"])]
       (when handle-envs?
         (dispatch [:environments.api/fetch-environments])))

     (dispatch [:transformations-search/add])
     (dispatch [:users.api/fetch-searchable-users])

     (dispatch [:environments.api/fetch-groups])

     (ui.modal/maximize "sql-console")

     ;; return unmount callback
     (fn []))
   #js [])

  (useHotkeys "mod+k" toggle-console-modal)

  [:f> ui.modal/modal {:id "sql-console"

                       :on-resize-stop scroll-to-current-tab

                       :header-props {:title "SQL console"
                                      :loading? @(re-posh/subscribe [:mold-http/loading? :sql-console.api/post-query])}
                       :footer footer

                       :open? @(re-frame/subscribe [:sql-console-modal.subs/modal-open?])
                       :on-close #(dispatch [:sql-console-modal.events/toggle-modal false])}
   [:f> console]
   [versions-list-modal]
   [revert-circular-dependency-modal]])


